import { DiscountUse, Models } from "api/types";

type Discount = Models<"DiscountModel">;
type DiscountLimits = Pick<Discount, "useType" | "usesRemaining" | "itemsRemaining">;

/** Per-client limits come back only for V2/campaign discounts; V1 discounts carry just `useType`. */
export const hasPerClientLimits = (discount?: DiscountLimits | null): boolean =>
  discount?.usesRemaining != null || discount?.itemsRemaining != null;

/** A per-client limit is spent: no uses or no items left (null means unlimited, not spent). */
export const isDiscountExhausted = (discount?: DiscountLimits | null): boolean =>
  discount?.usesRemaining === 0 || discount?.itemsRemaining === 0;

/**
 * Whether a discount that was already redeemed can be applied again: an unlimited-use offer, or
 * one with per-client limits that still have room. An exhausted limit is never re-appliable.
 */
export const isDiscountReappliable = (discount?: DiscountLimits | null): boolean =>
  !isDiscountExhausted(discount) &&
  (discount?.useType === DiscountUse.Unlimited || hasPerClientLimits(discount));

/** The backend sends promotions without a promo code (free-package and legacy ones) with `promoCodeModel` null. */
type PromotionUsage = {
  redeemedOn?: string | null;
  isDiscountUsed?: boolean;
  promoCodeModel?: { discount?: DiscountLimits | null } | null;
};

/**
 * Whether a promotion has nothing left to offer. With per-client limits that is an exhausted
 * limit, so a used-once N-use offer stays; without them (V1) it is any redemption.
 */
export const isPromotionSpent = (promotion: PromotionUsage): boolean => {
  const discount = promotion.promoCodeModel?.discount;
  return hasPerClientLimits(discount) ? isDiscountExhausted(discount) : !!promotion.redeemedOn;
};

/** Whether the backend's used flag should hide a promotion: with per-client limits only an exhausted one. */
export const isPromotionDiscountUsed = (promotion: PromotionUsage): boolean => {
  const discount = promotion.promoCodeModel?.discount;
  return hasPerClientLimits(discount) ? isDiscountExhausted(discount) : !!promotion.isDiscountUsed;
};
